import React, { useState } from "react";
import { customFetch } from "../../utils/Auth";
import { environment } from "../../config/environment";

const padLeft = (value, length = 2) => String(value).padStart(length, "0");

const formatLocalTimestamp = (date) => {
  const year = date.getFullYear();
  const month = padLeft(date.getMonth() + 1);
  const day = padLeft(date.getDate());
  const hours = padLeft(date.getHours());
  const minutes = padLeft(date.getMinutes());
  const seconds = padLeft(date.getSeconds());
  const microseconds = padLeft(date.getMilliseconds() * 1000, 6);

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${microseconds}`;
};

const formatShortDate = (date) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

// Mapping from the specialty label that appears on incoming license
// requisition sheets to the canonical specialty value stored in our DB.
// Keys are normalized (lowercased, non-alphanumerics stripped) so that
// minor punctuation/whitespace differences in the source sheet don't
// break the match.
const SPECIALTY_MAPPINGS = [
  { inputs: ["Cardiology"], db: "Cardiology" },
  { inputs: ["Neurology"], db: "Neurology" },
  { inputs: ["Orthopedic", "Orthopedics"], db: "Orthopedics" },
  { inputs: ["Gastroenterology", "Gastro"], db: "Gastroenterology" },
  { inputs: ["Psychiatry"], db: "Psychiatry" },
  {
    inputs: ["Psychology", "Psychologist", "Mental Health"],
    db: "Psychology",
  },
  {
    inputs: [
      "Physical Medicine Rehab",
      "Physical Medicine and Rehab",
      "Physical Medicine and Rehabilitation",
      "PM&R",
      "PMR",
    ],
    db: "PMR",
  },
  { inputs: ["Nephrology"], db: "Nephrology" },
  { inputs: ["Urology"], db: "Urology" },
  {
    inputs: [
      "Hematology/Oncology",
      "Hematology Oncology",
      "Heme/Onc",
      "Heme Onc",
      "Oncology"
    ],
    db: "Hematology Oncology",
  },
  { inputs: ["General Surgery"], db: "General Surgery" },
  {
    inputs: [
      "Ear, Nose, and Throat (ENT)",
      "Ear, Nose, and Throat",
      "Ear Nose and Throat",
      "ENT",
      "Otolaryngology",
    ],
    db: "Otolaryngology",
  },
  { inputs: ["Podiatry"], db: "Podiatry" },
  { inputs: ["Dermatology"], db: "Dermatology" },
  { inputs: ["Primary Care", "VA Tech Sprint"], db: "VA Tech Sprint" },
];

export const VA_SPECIALTY_OPTIONS = Array.from(
  new Set(SPECIALTY_MAPPINGS.map(({ db }) => db))
);

export const DEFAULT_SPECIALTY = "VA Tech Sprint";

const normalizeSpecialtyKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const SPECIALTY_LOOKUP = SPECIALTY_MAPPINGS.reduce((acc, { inputs, db }) => {
  inputs.forEach((label) => {
    acc[normalizeSpecialtyKey(label)] = db;
  });
  acc[normalizeSpecialtyKey(db)] = db;
  return acc;
}, {});

const resolveSpecialty = (rawValue) => {
  const key = normalizeSpecialtyKey(rawValue);
  if (!key) {
    return { db: DEFAULT_SPECIALTY, matched: false, raw: "" };
  }
  const db = SPECIALTY_LOOKUP[key];
  if (db) {
    return { db, matched: true, raw: String(rawValue).trim() };
  }
  return { db: DEFAULT_SPECIALTY, matched: false, raw: String(rawValue).trim() };
};

const escapeCSVValue = (value, delimiter) => {
  const stringValue = String(value ?? "");
  if (
    stringValue.includes('"') ||
    stringValue.includes("\n") ||
    stringValue.includes("\r") ||
    stringValue.includes(delimiter)
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

// Simple CSV parser - no dependencies needed
const parseCSVWithMeta = (text) => {
  const lines = text.trim().split("\n");
  if (lines.length === 0) return [];

  // Detect delimiter (tab or comma) from first line
  const firstLine = lines[0];
  const delimiter = firstLine.includes("\t") ? "\t" : ",";

  const parseLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headersOriginal = parseLine(lines[0]);
  const headersLower = headersOriginal.map((h) => h.toLowerCase());

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values = parseLine(line);
    rows.push(values);
  }

  return {
    headersOriginal,
    headersLower,
    rows,
    delimiter,
  };
};

export default function BulkRegisterVADoctors() {
  const [parsedDoctors, setParsedDoctors] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [results, setResults] = useState(null);
  const [rawCsvText, setRawCsvText] = useState("");
  const [csvMeta, setCsvMeta] = useState(null);
  const [updatedCsvText, setUpdatedCsvText] = useState("");

  const handleTextPaste = (e) => {
    const text = e.target.value;
    setRawCsvText(text);
    setUpdatedCsvText("");
    if (!text.trim()) {
      setParsedDoctors([]);
      setShowPreview(false);
      return;
    }
    processCSVText(text);
  };

  const processCSVText = (text) => {
    try {
      const csvData = parseCSVWithMeta(text);
      if (!csvData || csvData.rows.length === 0) {
        setMessage(
          "❌ No valid rows found. Make sure your CSV includes headers and data."
        );
        setShowPreview(false);
        setParsedDoctors([]);
        return;
      }

      // Transform CSV data into doctor objects
      // Expected columns: facility, provider email, secid
      const doctors = csvData.rows
        .map((values, index) => {
          const row = {};
          csvData.headersLower.forEach((header, headerIndex) => {
            row[header] = values[headerIndex] || "";
          });

          const secid =
            row.secid || row["sec id"] || row.fediamsecid || row.npi || "";
          const providerName = row["provider name"] || row.name || "";
          const userEmail =
            row["provider email"] ||
            row.provideremail ||
            row.useremail ||
            row.email ||
            "";
          const organization =
            row.facility || row.organization || row.org || row.location || "";
          const rawSpecialty =
            row.specialty ||
            row["provider specialty"] ||
            row["service line"] ||
            row.service ||
            "";

          // Pad secid to 10 digits for preview (backend will also do this)
          const paddedSecid = secid.trim().padStart(10, "0");
          const specialtyResolution = resolveSpecialty(rawSpecialty);
          const normalizedUserEmail = userEmail.trim().toLowerCase();

          return {
            id: index,
            rawIndex: index,
            npi: paddedSecid,
            providerName: providerName.trim(),
            organization: organization.trim(),
            userEmail: normalizedUserEmail,
            specialtyRaw: specialtyResolution.raw,
            specialty: specialtyResolution.db,
            specialtyMatched: specialtyResolution.matched,
          };
        })
        .filter((doc) => doc.npi && doc.organization && doc.userEmail);

      if (doctors.length === 0) {
        setMessage(
          "❌ No valid doctors found. Make sure your CSV has headers: Facility, Provider Email, SECID"
        );
        setShowPreview(false);
        setParsedDoctors([]);
        return;
      }

      setParsedDoctors(doctors);
      setShowPreview(true);
      setMessage("");
      setResults(null);
      setCsvMeta(csvData);
    } catch (error) {
      setMessage(`❌ Error parsing CSV: ${error.message}`);
      setShowPreview(false);
      setParsedDoctors([]);
      setCsvMeta(null);
    }
  };

  const handleBulkRegister = async () => {
    if (parsedDoctors.length === 0) {
      setMessage("❌ No valid doctors to register");
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const now = new Date();
      const activatedDateLocal = formatLocalTimestamp(now);
      const activatedDateDisplay = formatShortDate(now);

      // Transform to match backend expectations
      const doctorsForBackend = parsedDoctors.map((doc) => ({
        name: doc.providerName,
        organization: doc.organization,
        npi: doc.npi,
        userEmail: doc.userEmail,
        specialty: doc.specialty || DEFAULT_SPECIALTY,
        superOrg: "VA",
        activatedDateLocal,
        activatedDateDisplay,
      }));

      const response = await customFetch(
        `${environment.apiUrl}/doctors/bulk-register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: localStorage.token,
          },
          body: JSON.stringify({
            doctors: doctorsForBackend,
            username: localStorage.getItem("username") || "Unknown Admin",
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setMessage(`✅ ${data.message}`);
        setResults(data.results);

        if (data.results.successful.length > 0 && csvMeta) {
          const headersLower = csvMeta.headersLower;
          const activatedDateIndex = headersLower.findIndex(
            (header) => header === "activated date"
          );
          const emailIndex = headersLower.findIndex((header) =>
            ["provider email", "user email", "email"].includes(header)
          );
          const facilityIndex = headersLower.findIndex((header) =>
            ["facility", "organization", "org", "location"].includes(header)
          );
          const secidIndex = headersLower.findIndex((header) =>
            ["secid", "sec id", "fediamsecid", "npi"].includes(header)
          );

          const successDatesByEmail = new Map(
            data.results.successful
              .filter((doc) => doc.email)
              .map((doc) => [doc.email.toLowerCase(), doc.activatedDateDisplay])
          );

          const updatedHeaders =
            activatedDateIndex === -1
              ? [...csvMeta.headersOriginal, "Activated Date"]
              : csvMeta.headersOriginal;

          const updatedRows = csvMeta.rows.map((values) => {
            const normalizedValues = [...values];
            if (activatedDateIndex === -1) {
              normalizedValues.push("");
            }

            if (emailIndex !== -1) {
              const rowEmail = (normalizedValues[emailIndex] || "")
                .trim()
                .toLowerCase();
              if (successDatesByEmail.has(rowEmail)) {
                const targetIndex =
                  activatedDateIndex === -1
                    ? updatedHeaders.length - 1
                    : activatedDateIndex;
                normalizedValues[targetIndex] = successDatesByEmail.get(
                  rowEmail
                );
              }
            }

            return normalizedValues;
          });

          const hasValue = (value) => String(value || "").trim().length > 0;
          const lastDataRowIndex = updatedRows.reduce((lastIndex, row, idx) => {
            if (
              (emailIndex !== -1 && hasValue(row[emailIndex])) ||
              (facilityIndex !== -1 && hasValue(row[facilityIndex])) ||
              (secidIndex !== -1 && hasValue(row[secidIndex]))
            ) {
              return idx;
            }
            return lastIndex;
          }, -1);

          const delimiter = csvMeta.delimiter;
          const headerLine = updatedHeaders
            .map((value) => escapeCSVValue(value, delimiter))
            .join(delimiter);
          const rowsToWrite =
            lastDataRowIndex >= 0
              ? updatedRows.slice(0, lastDataRowIndex + 1)
              : [];
          const bodyLines = rowsToWrite.map((row) =>
            row
              .map((value) => escapeCSVValue(value, delimiter))
              .join(delimiter)
          );
          setUpdatedCsvText([headerLine, ...bodyLines].join("\n"));
        }
      } else {
        setMessage(`❌ Error: ${data.error || data}`);
      }
    } catch (err) {
      console.error("Error in bulk registration:", err);
      setMessage(`❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const removeParsedDoctor = (id) => {
    setParsedDoctors(parsedDoctors.filter((doc) => doc.id !== id));
  };

  const clearAll = () => {
    setParsedDoctors([]);
    setShowPreview(false);
    setMessage("");
    setResults(null);
    setRawCsvText("");
    setCsvMeta(null);
    setUpdatedCsvText("");
    const textarea = document.getElementById("bulk-csv-text-input");
    if (textarea) textarea.value = "";
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-md mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">📋 Instructions:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>
            • Paste data with these columns:{" "}
            <strong>Facility, Provider Email, SECID</strong> (all required)
          </li>
          <li>
            • <strong>Facility</strong>: Station code (e.g., 520)
          </li>
          <li>
            • <strong>Provider Email</strong>: Email address of the provider
          </li>
          <li>
            • <strong>SECID</strong>: FEDIAMSecID / NPI number
          </li>
          <li>
            • <strong>Specialty</strong> (optional): Pulled from the
            requisition sheet; mapped to the canonical DB value (see list
            below). Unrecognized or blank values fall back to{" "}
            <strong>{DEFAULT_SPECIALTY}</strong>.
          </li>
          <li>
            • <strong>Activated Date</strong>: Filled in after registration
          </li>
          <li>
            • All users are assigned super_org: <strong>VA</strong>
          </li>
        </ul>

        <details className="mt-3">
          <summary className="text-sm font-semibold text-blue-900 cursor-pointer">
            Specialty mapping (sheet value → DB value)
          </summary>
          <ul className="text-xs text-blue-800 mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4">
            {SPECIALTY_MAPPINGS.map(({ inputs, db }) => (
              <li key={db}>
                • <strong>{inputs[0]}</strong> → {db}
              </li>
            ))}
          </ul>
        </details>
      </div>

      {/* Paste Section */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Paste CSV Data
          </label>
          <textarea
            id="bulk-csv-text-input"
            onChange={handleTextPaste}
            placeholder="Paste CSV data here (with headers)&#10;Facility,Provider Email,SECID,Provider Name,Specialty&#10;520,rachel.duncan@va.gov,1018863116,Rachel K Duncan,Cardiology&#10;640,alex.smith@va.gov,1234567890,Alex Smith,Ear, Nose, and Throat (ENT)"
            rows="6"
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple font-mono text-sm"
            value={rawCsvText}
          />
        </div>
      </div>

      {/* Preview Section */}
      {showPreview && parsedDoctors.length > 0 && (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800">
              Preview: {parsedDoctors.length} Users
            </h3>
            <button
              onClick={clearAll}
              className="text-red-600 hover:text-red-800 text-sm font-semibold"
            >
              Clear All
            </button>
          </div>

          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SECID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Facility
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Specialty
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {parsedDoctors.map((doctor) => (
                  <tr key={doctor.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {doctor.npi}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {doctor.userEmail}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {doctor.organization}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {doctor.providerName || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="font-medium">{doctor.specialty}</div>
                      {doctor.specialtyRaw &&
                        doctor.specialtyRaw.toLowerCase() !==
                          doctor.specialty.toLowerCase() && (
                          <div
                            className={`text-xs ${
                              doctor.specialtyMatched
                                ? "text-gray-500"
                                : "text-yellow-700"
                            }`}
                          >
                            {doctor.specialtyMatched
                              ? `from "${doctor.specialtyRaw}"`
                              : `⚠️ unrecognized "${doctor.specialtyRaw}" → default`}
                          </div>
                        )}
                      {!doctor.specialtyRaw && (
                        <div className="text-xs text-gray-500">
                          no value → default
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => removeParsedDoctor(doctor.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6">
            <button
              onClick={handleBulkRegister}
              disabled={loading || parsedDoctors.length === 0}
              className="w-full bg-ktx-purple text-white py-3 px-4 rounded-md hover:bg-ktx-navy disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold text-lg"
            >
              {loading
                ? "Registering..."
                : `Register ${parsedDoctors.length} VA Users`}
            </button>
          </div>
        </div>
      )}

      {/* Message Display */}
      {message && (
        <div
          className={`p-4 rounded-md mb-6 ${
            message.includes("✅")
              ? "bg-green-100 text-green-800 border border-green-200"
              : "bg-red-100 text-red-800 border border-red-200"
          }`}
        >
          {message}
        </div>
      )}

      {/* Results Display */}
      {results && (
        <div className="bg-white shadow-md rounded-lg p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            Registration Results
          </h3>

          {results.successful.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold text-green-700 mb-2">
                ✅ Successfully Created ({results.successful.length}):
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 pl-4">
                {results.successful.map((doc, idx) => (
                  <li key={idx}>
                    • {doc.name} ({doc.email}) - {doc.organization}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {results.skipped.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold text-yellow-700 mb-2">
                ⚠️ Skipped ({results.skipped.length}):
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 pl-4">
                {results.skipped.map((doc, idx) => (
                  <li key={idx}>
                    • {doc.name} - {doc.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {results.failed.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold text-red-700 mb-2">
                ❌ Failed ({results.failed.length}):
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 pl-4">
                {results.failed.map((doc, idx) => (
                  <li key={idx}>
                    • {doc.name} - {doc.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {results.unassigned.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold text-green-300 mb-2">
                ✅ Unassigned ({results.unassigned.length}):
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 pl-4">
                {results.unassigned.map((doc, idx) => (
                  <li key={idx}>
                    • {doc.name} - {doc.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {updatedCsvText && (
            <div className="mt-6">
              <h4 className="font-semibold text-gray-800 mb-2">
                Updated CSV (Activated Date filled)
              </h4>
              <textarea
                readOnly
                value={updatedCsvText}
                rows="8"
                className="w-full p-3 border border-gray-300 rounded-md font-mono text-sm bg-gray-50"
              />
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(updatedCsvText);
                    setMessage("✅ Updated CSV copied to clipboard");
                  } catch (error) {
                    setMessage("❌ Unable to copy updated CSV");
                  }
                }}
                className="mt-3 w-full bg-ktx-navy text-white py-2 px-4 rounded-md hover:bg-ktx-purple font-semibold"
              >
                Copy Updated CSV
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
